import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Paperclip, SendHorizontal, X } from "lucide-react";
import { Button, Input, NoticeBox } from "../ui";
import { categoryLabel } from "./copilotText";
import { addFiles, formatFileSize } from "./format";
import { Assistant, Flow, Quick, QuickReplies, UserBubble } from "./FlowParts";
import { MESSAGE_MAX, STEPS, firstName } from "./intakeModel";

const ACCEPT = "image/jpeg,image/png,image/webp,application/pdf";

// The reference is shown inside the sentence as one left-to-right, unbreakable piece, so it never
// splits across lines or flips in Arabic.
function receivedSentence(g, reference) {
  const marker = "@@REFERENCE@@";
  const [before, after = ""] = g.confirmReceived(marker).split(marker);
  return (
    <>
      {before}
      <bdi dir="ltr" className="lc-nowrap">{reference}</bdi>
      {after}
    </>
  );
}

// The FAQ the customer found unhelpful, kept as context so it is not asked again.
function FaqContext({ g, faq, lang }) {
  const [open, setOpen] = useState(false);
  const question = faq.question?.[lang] || faq.question?.en || "";
  const answer = faq.answer?.[lang] || faq.answer?.en || "";
  return (
    <UserBubble g={g} className="lc-faq-context">
      <span className="lc-context-label">{g.yourQuestion}</span>
      <p>{question}</p>
      {answer && (
        <>
          <Button variant="neutral" appearance="text" size="xs" className="lc-faq-toggle" aria-expanded={open} onClick={() => setOpen(!open)} icon={open ? <ChevronUp size={13} aria-hidden="true" /> : <ChevronDown size={13} aria-hidden="true" />}>
            {open ? g.hideAnswer : g.showAnswer}
          </Button>
          {open && <p className="lc-context-answer">{answer}</p>}
        </>
      )}
    </UserBubble>
  );
}

function Summary({ c, lang, name }) {
  const { g, intake, mode, visibleCategories, restaurants, intakeInfo: info } = c;
  const rows = [[g.fieldCategory, categoryLabel(visibleCategories, intake.category, lang)]];
  if (info.needsRestaurant && intake.restaurant) rows.push([g.fieldRestaurant, restaurants.find((r) => r.value === intake.restaurant)?.label || intake.restaurant]);
  rows.push([g.fieldMessage, intake.message]);
  if (mode === "guest") rows.push([g.fieldName, name], [g.fieldEmail, intake.email]);
  const phone = intake.phone || info.accountPhone;
  if (phone) rows.push([g.fieldPhone, phone]);
  if (intake.faq?.question) rows.push([g.fieldFaq, intake.faq.question[lang] || intake.faq.question.en]);
  return (
    <dl className="lc-summary">
      {rows.map(([label, value]) => (
        <div key={label} className={label === g.fieldMessage ? "wide" : undefined}>
          <dt>{label}</dt>
          <dd dir={label === g.fieldEmail || label === g.fieldPhone ? "ltr" : undefined}>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

// The guided support request. One question at a time, answered with buttons or one input; every
// bubble is derived from the draft, so refreshes and re-renders never repeat a question. Nothing is
// created (and no e-mail is sent) until the customer confirms on the review step.
export default function GuidedIntake({ c, lang, userName }) {
  const { g, t, intake, mode } = c;
  const info = c.intakeInfo;
  const steps = info.steps;
  const [fieldError, setFieldError] = useState(null);
  const [fileError, setFileError] = useState(null);
  const inputRef = useRef(null);
  const fileInput = useRef(null);
  const sending = intake.status === "submitting";
  const name = firstName(intake.name || userName);

  useEffect(() => {
    setFieldError(null);
    setFileError(null);
    inputRef.current?.focus?.();
  }, [intake.step]);

  // ---- confirmation ---------------------------------------------------------------------------
  if (intake.status === "done" && intake.done) {
    const { reference, hasAccess, id, attachments } = intake.done;
    return (
      <Flow g={g} scrollKey="done">
        <Assistant g={g} className="lc-confirm">
          <p className="lc-flow-title">{g.confirmTitle(name)}</p>
          <p>{receivedSentence(g, reference)}</p>
          <p className="lc-ref" dir="ltr">{reference}</p>
          <p>{g.confirmNext}</p>
          {hasAccess ? <p>{g.confirmReturn}</p> : <p>{g.confirmNoAccess}</p>}
          {mode === "guest" && hasAccess && <p>{g.confirmGuest}</p>}
          {attachments && <p>{g.attachmentsNote}</p>}
          <p>{g.confirmTrust}</p>
        </Assistant>
        <QuickReplies>
          {hasAccess && <Quick primary onClick={() => c.intakeOpenConversation(id)}>{g.openConversation}</Quick>}
          <Quick onClick={c.intakeFinish}>{g.backToStart}</Quick>
        </QuickReplies>
      </Flow>
    );
  }

  const submit = (event) => {
    event?.preventDefault();
    if (sending) return;
    const result = c.answerIntake(intake.step, intake.input);
    if (result.error) {
      setFieldError(result.error);
      inputRef.current?.focus?.();
    }
  };

  const onFiles = (event) => {
    const result = addFiles(c.intakeFiles, Array.from(event.target.files || []), t);
    c.addIntakeFiles(result.files);
    setFileError(result.error);
    event.target.value = "";
  };

  // ---- the conversation so far ----------------------------------------------------------------
  const currentIndex = Math.max(0, steps.indexOf(intake.step));
  const answered = intake.step === STEPS.EDIT || intake.step === STEPS.REVIEW ? steps.filter((s) => s !== STEPS.REVIEW) : steps.slice(0, currentIndex);
  const restaurantLabel = (value) => c.restaurants.find((r) => r.value === value)?.label || g.noRestaurant;

  const prompt = (step) => {
    switch (step) {
      case STEPS.CATEGORY: return g.categoryPrompt;
      case STEPS.RESTAURANT: return g.restaurantPrompt;
      case STEPS.MESSAGE: return intake.faq ? g.messagePromptFaq : g.messagePrompt;
      case STEPS.NAME: return g.namePrompt;
      case STEPS.EMAIL: return name ? g.emailPrompt(name) : g.emailPromptNoName;
      case STEPS.PHONE: return g.phonePrompt(name);
      default: return "";
    }
  };
  const answerText = (step) => {
    switch (step) {
      case STEPS.CATEGORY: return categoryLabel(c.visibleCategories, intake.category, lang);
      case STEPS.RESTAURANT: return intake.restaurant ? restaurantLabel(intake.restaurant) : g.noRestaurant;
      case STEPS.MESSAGE: return intake.message;
      case STEPS.NAME: return intake.name;
      case STEPS.EMAIL: return intake.email;
      case STEPS.PHONE: return intake.phone;
      default: return "";
    }
  };
  const ltr = (step) => step === STEPS.EMAIL || step === STEPS.PHONE;

  const transcript = [];
  if (intake.faq) {
    transcript.push(<FaqContext key="faq" g={g} faq={intake.faq} lang={lang} />);
    transcript.push(<Assistant g={g} key="faq-lead">{g.escalateLead}</Assistant>);
    if (info.categoryKnown) transcript.push(<Assistant g={g} key="faq-category">{g.categoryPrefilled(categoryLabel(c.visibleCategories, intake.category, lang))}</Assistant>);
  }
  answered.forEach((step) => {
    transcript.push(<Assistant g={g} key={`q-${step}`}>{prompt(step)}</Assistant>);
    transcript.push(<UserBubble g={g} key={`a-${step}`}><p className={ltr(step) ? "lc-ltr" : undefined} dir={ltr(step) ? "ltr" : undefined}>{answerText(step)}</p></UserBubble>);
  });

  // ---- the current step ---------------------------------------------------------------------------
  let current = null;
  let input = null;
  const step = intake.step;

  if (intake.confirmCancel) {
    current = (
      <>
        <Assistant g={g}>{g.discardQuestion}</Assistant>
        <QuickReplies>
          <Quick primary onClick={c.intakeDiscard}>{g.discardYes}</Quick>
          <Quick onClick={c.intakeKeepGoing}>{g.keepGoing}</Quick>
        </QuickReplies>
      </>
    );
  } else if (step === STEPS.CATEGORY) {
    current = (
      <>
        <Assistant g={g}>{g.categoryPrompt}</Assistant>
        {fieldError && <Assistant g={g} tone="error">{fieldError}</Assistant>}
        <QuickReplies label={g.categoryPrompt}>
          {c.visibleCategories.map((cat) => (
            <Quick key={cat.value} onClick={() => c.answerIntake(STEPS.CATEGORY, cat.value)}>{lang === "ar" ? cat.labelAr : cat.labelEn}</Quick>
          ))}
        </QuickReplies>
      </>
    );
  } else if (step === STEPS.RESTAURANT) {
    current = (
      <>
        <Assistant g={g}>{prompt(step)}</Assistant>
        <QuickReplies label={prompt(step)}>
          {c.restaurants.map((r) => <Quick key={r.value} onClick={() => c.answerIntake(STEPS.RESTAURANT, r.value)}>{r.label}</Quick>)}
          <Quick onClick={() => c.answerIntake(STEPS.RESTAURANT, "")}>{g.noRestaurant}</Quick>
        </QuickReplies>
      </>
    );
  } else if (step === STEPS.EDIT) {
    current = (
      <>
        <Assistant g={g}>{g.editWhich}</Assistant>
        <QuickReplies label={g.editWhich}>
          {info.editable.map((target) => {
            const label = { category: g.fieldCategory, restaurant: g.fieldRestaurant, message: g.fieldMessage, name: g.fieldName, email: g.fieldEmail, phone: g.fieldPhone }[target];
            return <Quick key={target} onClick={() => c.intakeEditField(target)}>{label}</Quick>;
          })}
        </QuickReplies>
      </>
    );
  } else if (step === STEPS.REVIEW) {
    current = (
      <>
        <Assistant g={g}>
          <p>{g.reviewIntro}</p>
          <Summary c={c} lang={lang} name={intake.name} />
          {c.intakeFiles.length > 0 && <p className="lc-muted-line">{c.intakeFiles.map((f) => f.name).join(", ")}</p>}
        </Assistant>
        {intake.error && <Assistant g={g} tone="error">{intake.error.text}</Assistant>}
        {sending && <Assistant g={g}><span className="lc-muted-line" role="status">{g.sendingRequest}</span></Assistant>}
        <QuickReplies>
          <Quick primary onClick={c.submitIntake} disabled={sending}>{intake.error ? g.tryAgain : g.sendRequest}</Quick>
          <Quick onClick={c.intakeEdit} disabled={sending}>{g.editDetails}</Quick>
        </QuickReplies>
      </>
    );
  } else {
    // message / name / email / phone: the prompt, and one input at the bottom
    current = (
      <>
        <Assistant g={g}>{prompt(step)}</Assistant>
        {fieldError && <Assistant g={g} tone="error">{fieldError}</Assistant>}
      </>
    );
    const hint = step === STEPS.EMAIL ? g.emailHint : step === STEPS.PHONE ? g.phoneHint : null;
    const common = {
      id: "lc-intake-input",
      ref: inputRef,
      value: intake.input,
      onChange: (event) => {
        setFieldError(null);
        c.setIntakeInput(event.target.value);
      },
      "aria-label": g.answerLabel,
      error: Boolean(fieldError),
      disabled: sending,
    };
    input = (
      <form className="lc-answer-form" onSubmit={submit} noValidate>
        {step === STEPS.MESSAGE ? (
          <>
            <Input
              textarea
              {...common}
              rows={3}
              placeholder={g.messagePlaceholder}
              onKeyDown={(event) => {
                if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) submit(event);
              }}
            />
            <input ref={fileInput} type="file" hidden multiple accept={ACCEPT} onChange={onFiles} />
            <div className="lc-answer-tools">
              <Button variant="primary" appearance="text" size="xs" icon={<Paperclip size={14} aria-hidden="true" />} onClick={() => fileInput.current?.click()} disabled={sending}>{g.attach}</Button>
              {intake.input.length > MESSAGE_MAX - 500 && <span className={`lc-counter ${intake.input.length > MESSAGE_MAX ? "over" : ""}`} dir="ltr">{intake.input.length} / {MESSAGE_MAX}</span>}
            </div>
            {fileError && <NoticeBox variant="danger" size="sm">{fileError}</NoticeBox>}
            {c.intakeFiles.length > 0 && (
              <ul className="lc-files">
                {c.intakeFiles.map((file, index) => (
                  <li key={`${file.name}-${index}`}>
                    <span className="lc-file-name">{file.name}</span>
                    <span className="lc-file-size">{formatFileSize(file.size)}</span>
                    <Button variant="neutral" appearance="ghost" size="xs" aria-label={`${g.removeFile}: ${file.name}`} icon={<X size={14} aria-hidden="true" />} onClick={() => c.addIntakeFiles(c.intakeFiles.filter((_, i) => i !== index))} />
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <>
            <Input
              {...common}
              helperText={hint || undefined}
              type={step === STEPS.EMAIL ? "email" : step === STEPS.PHONE ? "tel" : "text"}
              dir={ltr(step) ? "ltr" : undefined}
              autoComplete={step === STEPS.EMAIL ? "email" : step === STEPS.PHONE ? "tel" : "name"}
              maxLength={step === STEPS.NAME ? 100 : step === STEPS.EMAIL ? 254 : 30}
              placeholder={step === STEPS.NAME ? g.namePlaceholder : step === STEPS.EMAIL ? g.emailPlaceholder : g.phonePlaceholder}
              inputMode={step === STEPS.PHONE ? "tel" : step === STEPS.EMAIL ? "email" : undefined}
            />
          </>
        )}
        <Button type="submit" variant="primary" appearance="filled" rounded className="lc-send" aria-label={step === STEPS.MESSAGE ? g.send : g.next} title={step === STEPS.MESSAGE ? g.send : g.next} disabled={sending || !intake.input.trim()} icon={<SendHorizontal size={18} aria-hidden="true" />} />
      </form>
    );
  }

  const showNav = !intake.confirmCancel && !sending;
  const footer = (
    <div className="lc-flow-foot-wrap">
      {input}
      {showNav && (
        <div className="lc-flow-foot">
          <Button variant="neutral" appearance="text" size="sm" onClick={c.intakeBack}>{g.back}</Button>
          <Button variant="neutral" appearance="text" size="sm" onClick={c.intakeCancel}>{g.cancelRequest}</Button>
        </div>
      )}
    </div>
  );

  return (
    <Flow g={g} footer={footer} scrollKey={`${step}:${fieldError}:${intake.status}:${intake.confirmCancel}:${Boolean(intake.error)}`}>
      {transcript}
      {current}
    </Flow>
  );
}
